import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';
import { Icon, Tooltip } from '~/components';
import { useFeedback } from '~/contexts';
import { colors, weights } from '~/styles';

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: ${colors.grey25};
  font-size: 1rem;
  font-weight: ${weights.normal};
  cursor: pointer;
  transition: color 150ms;

  &:hover {
    background: transparent;
    color: ${colors.primary};
  }

  .is-open & {
    color: ${colors.primary};
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 0.75rem);
  right: 50%;
  transform: translateX(50%);
  display: none;
  flex-direction: column;
  min-width: 11rem;
  padding: 0.5rem 0;
  background-color: ${colors.white};
  border-radius: 0.625rem;
  box-shadow: 0 0.25rem 1.5rem ${rgba(colors.black, 0.15)};

  .is-open & {
    display: flex;
  }
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0;
  background: transparent;
  color: ${colors.black};
  font-size: 0.875rem;
  font-weight: ${weights.normal};
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;

  .icon {
    font-size: 0.75rem;
    color: ${colors.grey25};
    width: 0.875rem;
    text-align: center;
  }

  &:hover {
    background: ${colors.grey5};
    color: ${colors.primary};

    .icon {
      color: ${colors.primary};
    }
  }
`;

export default function HelpMenu() {
  const { openFeedbackModal } = useFeedback();
  const [isOpen, setOpen] = useState(false);
  const containerRef = useRef();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;

    function handleMouseUp(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [isOpen]);

  const handleSupportRequest = () => {
    close();
    window.location.href = 'mailto:help@ruddr.io';
  };

  const handleHelpCenter = () => {
    close();
    window.open('https://help.ruddr.io', '_blank');
  };

  const handleSubmitFeedback = () => {
    close();
    openFeedbackModal();
  };

  return (
    <Container ref={containerRef} className={classNames({ 'is-open': isOpen })}>
      <Tooltip
        message={isOpen ? undefined : 'Help & Support'}
        placement="bottom"
        offsetTop={13}
        Container={Trigger}
        onClick={() => setOpen(!isOpen)}>
        <Icon icon="circle-question" type="far" />
      </Tooltip>
      <Menu>
        <MenuItem onClick={handleHelpCenter}>
          <Icon icon="book" type="far" />
          Help Center
        </MenuItem>
        <MenuItem onClick={handleSupportRequest}>
          <Icon icon="message-plus" type="far" />
          Submit Support Request
        </MenuItem>
        <MenuItem onClick={handleSubmitFeedback}>
          <Icon icon="envelope" type="far" />
          Submit Feedback
        </MenuItem>
      </Menu>
    </Container>
  );
}
